import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { AnimatePresence, ScaleFadeIn } from './Animations';
import { cn } from './cn';

export interface SearchInputProps {
  placeholder?: string;
  /** Accessible name for the field. Defaults to `placeholder` when omitted. */
  ariaLabel?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
}

export const SearchInput = ({
  placeholder = 'Search...',
  ariaLabel,
  value = '',
  onChange,
  className,
}: SearchInputProps) => {
  const inputId = React.useId();
  const accessibleName = ariaLabel ?? placeholder;

  return (
    <div className={cn('relative', className)}>
      <label htmlFor={inputId} className="sr-only">
        {accessibleName}
      </label>
      <MagnifyingGlassIcon
        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary"
        aria-hidden
      />
      <input
        id={inputId}
        type="text"
        role="searchbox"
        className={cn(
          'h-9 w-full rounded-md border pl-9 pr-9 text-sm text-text-primary outline-none transition-all duration-200',
          'border-border bg-surface placeholder:text-text-muted',
          'focus:border-info-500 focus:ring-2 focus:ring-info-500/20 focus:shadow-sm'
        )}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
      />
      <AnimatePresence>
        {value && (
          <ScaleFadeIn
            className="absolute right-2.5 top-1/2 -translate-y-1/2"
            from={0.8}
            transition={{ duration: 0.15 }}
          >
            <button
              className="text-text-muted hover:text-text-primary cursor-pointer bg-transparent border-none p-0.5 rounded-md hover:bg-surface-hover transition-colors"
              onClick={() => onChange?.('')}
              aria-label="Clear search"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </ScaleFadeIn>
        )}
      </AnimatePresence>
    </div>
  );
};
